import React, {useState} from 'react';
import styled from "@emotion/styled";
import {css} from "@emotion/react"
import {Section} from "./content";
import {Icon} from "./Icon";
import {TariffsBadge} from './TariffsBadge';

import {Colors, contractAzul, eerieBlack, royalPurple, tartOrange, violetBlue} from "../styles/colors"
import {Typography} from "../styles/typography";
import {DESKTOP_MIN_WIDTH, isBrowser} from '../styles/utils';

import star from "../images/icons/star.svg"
import {Spacer} from "./spacer";
import {Button} from './button';
import {Spacing} from '../styles/spacing';
import {InformationContainer} from './informationContainer';
import checkmark from '../images/icons/checkmark.svg'
import chevronDownIcon from '../images/icons/chevron-down.svg'
import chevronUpIcon from '../images/icons/chevron-up.svg'

const hoverStyleButtonGas = css`:hover {
  background-color: ${eerieBlack['0']};
  color: ${royalPurple['500']};
}`

const hoverStyleButtonEnergy = css`:hover {
  background-color: ${eerieBlack['0']};
  color: ${contractAzul['500']};
}`

export const NewCardAction = ({contractType}) => {
  return <Button
    variant="none"
    className="d-inline-block"
    css={[
      contractType === 'energy' && hoverStyleButtonEnergy,
      contractType === 'gas' && hoverStyleButtonGas,
      `width: 100%; box-sizing: border-box;`,
      Colors.BackgroundColor.darkMint500,
      Colors.Color.eerieBlack0,
      Spacing.Margin.top_24,
      Spacing.Padding.right_48,
      Spacing.Padding.left_48
    ]}>
    <span css={[Typography.label.label1_700]}>Stima la bolletta</span>
  </Button>
}

/**
 * Container
 */
const StyledCardContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    flex-direction: ${props => props.column ? 'column' : 'row'};
    align-items: center;
    justify-content: space-between;
  }

  &[data-flip='true'] {
    & > div {
      flex-direction: row-reverse;
    }
  }
`

export const CardsContainer = ({children, flip = false, noSpacing = 'false', ...props}) => (
  <Section {...props} data-flip={String(flip)} data-no-spacing={String(noSpacing)}>
    <StyledCardContainer {...props}>
      {children}
    </StyledCardContainer>
  </Section>
)

/**
 * Card Container
 */
export const Card = styled.div`
  flex: 1;
  @media (max-width: ${DESKTOP_MIN_WIDTH}) {
    order: ${props => props.order ? props.order : -1};
  }
`

/**
 * Card Components
 * Title / SubTitle
 */
export const CardTitle = styled.h3`
  ${Typography.fonts.inter}
  ${Typography.heading.h4_700}
  color: ${eerieBlack['800']};
  margin-bottom: 24px;
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    ${Typography.heading.h3_700}
  }
`

export const CardSubTitle = styled.h5`
  ${Typography.fonts.inter}
  ${Typography.heading.h6_400}
  color: ${eerieBlack['800']};
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    ${Typography.heading.h5_400}
  }
`

export const ImageCard = styled.div`
  flex: 1;

  text-align: center;
  border-radius: 16px;
  background-color: ${props => props.backgroundColor || 'transparent'};

  .gatsby-image-wrapper {
    order: 1;
    justify-content: center;
    vertical-align: ${props => props.align ? props.align : 'center'};
  }
`
/** Tariff Card internal components */
const TariffType       = styled.div`
  ${Typography.label.label2_500}
  color: ${eerieBlack["600"]};
  text-transform: uppercase;
`

const PreferredTariffBadge = styled(({className}) => (
  <div className={className}>
    <div>
      <Icon src={star} css={css`width: 30px;
        height: 30px;
        margin-right: 8px;
        padding: 0px;
        margin-bottom: 2px;

        & > div {
          background-color: ${eerieBlack["0"]};
        } `}/><span>CONSIGLIATA</span>
    </div>
  </div>
))`
  display: flex;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: -24px;

  > div {
    > span {
      ${Typography.label.label1_600}
      color: ${eerieBlack["0"]}
    }

    width: 183px;
    border-radius: 6px;
    align-items: center;
    display: flex;
    background-color: ${tartOrange["500"]};
    padding: 8px 16px;
  }
`

const TariffDescription = styled.div`
  ${Typography.paragraph.p2_400}
  color: ${eerieBlack["700"]};

  > strong {
    ${Typography.paragraph.p2_600}
  }
`

const NewTariffDescription = ({descriptions = []}) => {
  return descriptions.map((description, index) => {
    return <div
      key={index}
      css={[
        Typography.label.label2_500,
        Spacing.Flex.vertical_center,
        Spacing.Flex.flex_1,
        Colors.Color.eerieBlack0
      ]}>
      <Icon src={checkmark} iconColor={eerieBlack['0']}/>
      {description}
    </div>
  })
}

const TariffFootNote = styled.div`
  ${Typography.paragraph.p3_400}
  margin-top: 8px;
  color: ${violetBlue["50"]};
  padding: 0px 8px;
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    color: ${eerieBlack["600"]};
    margin-top: 16px;
    padding: 0px;
  }
`


export const TariffCard = styled(({
                                    title,
                                    type,
                                    className,
                                    preferred = false,
                                    tariffs,
                                    description,
                                    footNote,
                                    contractType,
                                    colorHeader = false
                                  }) => (
  <div className={className} data-best={preferred} data-color={colorHeader}>
    <header data-type={contractType}>
      {type && <TariffType>{type}</TariffType>}
      {!type && <Spacer size={20}/>}
      <CardTitle>{title}</CardTitle>
    </header>
    <main>
      <TariffsBadge tariffs={tariffs} contractType={contractType}/>
      <TariffDescription>{description}</TariffDescription>
      {footNote && <TariffFootNote className="d-none d-lg-block">{footNote}</TariffFootNote>}
    </main>
    {preferred && <PreferredTariffBadge/>}
    {footNote && <TariffFootNote className="d-lg-none">{footNote}</TariffFootNote>}
  </div>
))`
  display: flex;
  position: relative;
  flex: 1;
  flex-direction: column;
  border-radius: 16px;
  @media (max-width: ${DESKTOP_MIN_WIDTH}) {
    order: ${props => props.order ? props.order : -1};
  }

  > header {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 32px 24px 0px 24px;
    background-color: ${eerieBlack["100"]};
    @media (min-width: ${DESKTOP_MIN_WIDTH}) {
      padding: 32px 32px 0px 32px;
    }
  }

  > main {
    padding: 16px 24px 24px 24px;
    background-color: ${eerieBlack["0"]};
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    @media (min-width: ${DESKTOP_MIN_WIDTH}) {
      padding: 24px 32px 32px 32px;

    }
  }

  ${CardTitle} {
    color: ${contractAzul["700"]};
  }

  &[data-best='true'] {
    > header {
      &[data-type='energy'] {
        background-color: ${contractAzul["700"]};
      }

      &[data-type='gas'] {
        background-color: ${royalPurple["700"]};
      }
    }

    ${TariffType} {
      color: ${violetBlue["100"]};
    }

    ${CardTitle} {
      color: ${eerieBlack["0"]};
    }
  }

  &[data-color='true'] {
    > header {
      &[data-type='energy'] {
        background-color: ${contractAzul["700"]};
      }

      &[data-type='gas'] {
        background-color: ${royalPurple["700"]};
      }
    }

    ${TariffType} {
      color: ${violetBlue["100"]};
    }

    ${CardTitle} {
      color: ${eerieBlack["0"]};
    }
  }
`

const powerPrice = 126

export const NewTariffCard = styled(({
                                       title,
                                       className,
                                       description,
                                       contractType,
                                       infoCards,
                                       footer
                                     }) => {
  return (<div data-type={contractType} className={className}>
    <div className='container'>
      <CardTitle>{title}</CardTitle>
      {
        infoCards.map((info, index) => {
          return <InformationContainer
            costs={powerPrice}
            key={index}
            divider={index < (infoCards.length - 1)}
            info={info} contractType={contractType}
          />
        })
      }
      <NewTariffDescription descriptions={description}/>
      {footer && footer}
    </div>
  </div>)
})`
  display: flex;
  position: relative;
  flex-direction: column;
  border-radius: 16px;
  @media (max-width: ${DESKTOP_MIN_WIDTH}) {
    order: ${props => props.order ? props.order : -1};
  }

  &[data-type='energy'] {
    background-color: ${contractAzul["700"]};
  }

  &[data-type='gas'] {
    background-color: ${royalPurple["700"]};
  }

  .container {
    box-sizing: border-box;
    flex-direction: column;
    padding: 24px;
    border-radius: 16px;
  }

  ${TariffType} {
    color: ${violetBlue["100"]};
  }

  ${CardTitle} {
    color: ${eerieBlack["0"]};
    ${Typography.heading.h6_700};
  }
`

